import EventListener, { EventHandler } from '../eventEmitter';

/** onError: 解码音频文件中的 ArrayBuffer 失败时会触发onError；播放的buffer长度为0会触发onError  */

/**
 * onEnded
 * 播放完成，包括正常播放完成和异常播放完成
 * 正常播放完成，会先触发onEnded，再触发onComplete
 * 异常播放完成是指第一次调用play还没播放完，再次调用play，则会触发onEnded
 * 一定会执行onEnded，不一定执行onComplete
 */

/** onSuspend 暂停事件，调用suspend方法时触发 */
/** onResume 恢复事件，调用resume方法时触发 */

type EventKeys = 'onResume' | 'onSuspend' | 'onComplete' | 'onPlay' | 'onEnded' | 'onError';

class WebAudio {
  audioCtx!: AudioContext | null;
  /** 开始播放时间，默认0 */
  #startPlayTime = 0;
  /** 当前播放的source */
  #source!: AudioBufferSourceNode | null;
  #eventListener: EventListener;
  constructor() {
    this.#eventListener = new EventListener();
  }

  static getCtx = () => {
    try {
      // @ts-ignore
      return wx?.createWebAudioContext();
    } catch (err) {}
    // @ts-ignore
    return new (window.AudioContext || window.webkitAudioContext)();
  };

  /** h5播放前需要调用此方法检测是否可以播放，小程序不需要 */
  static canPlay = (() => {
    let isCanPlay = false;
    return async () => {
      return new Promise((resolve, reject) => {
        try {
          // @ts-ignore
          const ctx = wx?.createWebAudioContext; // 暂且把能拿到wx信息的判断为小程序环境
          resolve(true);
          return;
        } catch (err) {}

        if (isCanPlay) {
          resolve(true);
          return;
        }
        const ctx = WebAudio.getCtx() as AudioContext;
        // 测试的buffer数据
        const audioBuffer = ctx.createBuffer(1, 1, 22050);
        // Create an empty buffer.
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);

        // Play the empty buffer.
        if (typeof source.start === 'undefined') {
          // @ts-ignore
          source?.noteOn?.(0);
        } else {
          source.start(0);
        }

        // Calling resume() on a stack initiated by user gesture is what actually unlocks the audio on Android Chrome >= 55.
        if (typeof ctx.resume === 'function') {
          ctx.resume();
        }

        // duration的值为：0.000045351473922902495
        const duration = audioBuffer.duration;

        // Setup a timeout to check that we are unlocked on the next event loop.
        source.onended = function () {
          source.disconnect(0);
          isCanPlay = true;
          // 返回true，就是可以播放了
          resolve(true);
        };
        // 超时返回false，表示需要用户进行交互后才能播放
        setTimeout(() => {
          resolve(false);
        }, duration + 16);
      });
    };
  })();

  static isSupportAudioContext() {
    const wxSupport = () => {
      try {
        // @ts-ignore
        return !!(wx && wx.createWebAudioContext);
      } catch (err) {
        return false;
      }
    };

    const h5Support = () => {
      // @ts-ignore
      return !!(window.AudioContext || window.webkitAudioContext);
    };
    return wxSupport() || h5Support();
  }

  #init = async () => {
    if (this.audioCtx) {
      await this.audioCtx.close();
      this.audioCtx = null;
    }
    // 有可能getCtx失败，需要try catch init方法
    this.audioCtx = WebAudio.getCtx();
    this.#startPlayTime = 0;
  };

  #closeSource = () => {
    if (this.#source) {
      this.#source.stop();
      this.#source.disconnect();
      this.#source = null;
      this.#eventListener.emit<EventKeys>('onEnded');
    }
  };

  addEventListener = (key: EventKeys, handler: EventHandler) => {
    this.#eventListener.on(key, handler);
  };

  removeEventListener = (key: EventKeys, handler: EventHandler) => {
    this.#eventListener.off(key, handler);
  };

  /** 同步恢复已经被暂停的WebAudioContext上下文 */
  resume = () => {
    if (!this.audioCtx) return;
    if (this.audioCtx.state === 'running') return;
    this.audioCtx.resume();
    this.#eventListener.emit<EventKeys>('onResume');
  };

  /** 同步暂停WebAudioContext上下文 */
  suspend = () => {
    if (!this.audioCtx) return;
    if (this.audioCtx.state === 'suspended') return;
    this.audioCtx.suspend();
    this.#eventListener.emit<EventKeys>('onSuspend');
  };

  /** 关闭一个音频环境，释放任何正在使用系统资源的音频。 */
  close = () => {
    if (!this.audioCtx) return;
    this.audioCtx.close();
    this.audioCtx = null;
    this.#source = null;
    this.#eventListener.emit<EventKeys>('onEnded');
  };

  /**
   * 播放语音
   * @param buffer
   * @returns true表示播放完成，false表示播放了，但是没有播放完成，停止、终止、其他异常导致没有播放完
   */
  play = async (buffer: ArrayBuffer): Promise<boolean> => {
    if (buffer?.byteLength === 0) {
      const errMsg = new Error('buffer不能为空');
      this.#eventListener.emit<EventKeys>('onError', errMsg);
      throw errMsg;
    }
    // 复制一份buffer，避免播放完后传入的buffer长度为0
    const copyBuffer = new Uint8Array(buffer.byteLength);
    copyBuffer.set(new Uint8Array(buffer), 0);

    this.#closeSource();
    // 只要播放就初始化一次，让webAudioContextRef.current处于最初状态
    try {
      await this.#init();
    } catch (e) {
      this.#eventListener.emit<EventKeys>('onError', e);
      throw e;
    }
    if (!this.audioCtx) {
      const errMsg = new Error('this.audioCtx 为空');
      this.#eventListener.emit<EventKeys>('onError', errMsg);
      throw errMsg;
    }

    const audioCtx = this.audioCtx;

    return new Promise((resolve, reject) => {
      audioCtx.decodeAudioData(
        copyBuffer.buffer,
        decodeBuffer => {
          const source = audioCtx.createBufferSource();
          this.#source = source;
          source.buffer = decodeBuffer;
          source.connect(audioCtx.destination);
          source.start();
          this.#eventListener.emit<EventKeys>('onPlay');
          // 开始播放时间
          this.#startPlayTime = audioCtx.currentTime;
          // buffer原始的总时间
          const originTotalTime = decodeBuffer.duration;
          source.onended = () => {
            this.#eventListener.emit<EventKeys>('onEnded');
            // 时间偏移量，大概给一个值
            const offsetTime = 0.1;
            // 总的播放时间
            const duration = audioCtx.currentTime - this.#startPlayTime + offsetTime;
            console.log('当前buffer播放总时间：', duration);
            console.log('当前buffer原始的总时间：', originTotalTime);

            // 终止播放也会执行 onended ，所以判断播放时间是否大于buffer原始的总时间
            // 如果是正常播放，总的播放时间会大于buffer原始的总时间
            if (duration >= originTotalTime) {
              this.#eventListener.emit<EventKeys>('onComplete');
              this.#source = null;
              resolve(true);
              return;
            }
            resolve(true);
          };
        },
        e => {
          this.#eventListener.emit<EventKeys>('onError', e);
          reject(e);
        }
      );
    });
  };
}

export default WebAudio;
